import { getPlaylistCovers } from '../digitalVault';
const parseNumber = (str) => Number(str);
const parseBoolean = (str) => Boolean(str);
const parseDate = (str) => String(new Date(str).valueOf());
/**
 * Parse named attributes and to object with camelcase names
 *
 * @param data attributes object or array
 * @param attributes object with camelcase keys
 * @returns
 */
export const parseNamedAttributes = (data = [], attributes = {}) => {
    for (const { '@name': name, $: value } of Array.isArray(data) ? data : [data]) {
        if (!value || value == '-') {
            break;
        }
        const key = name.toLowerCase().replace(/-./g, (g) => g.substring(1).toUpperCase());
        if (key === 'favorite') {
            attributes[key] = parseBoolean(value);
            continue;
        }
        attributes[key] = value;
    }
    return attributes;
};
// Parser map
const parsers = {
    attributesChanged: parseBoolean,
    deleted: parseBoolean,
    size: parseNumber,
    version: parseNumber,
    systemAttribute: parseNamedAttributes,
    versionCreated: parseDate,
};
/**
 * Traverse api response data
 *
 * @param data object to parse
 * @param node root node
 * @returns object
 */
const traverse = (data = {}, node = {}) => {
    // ignore non objects
    if (!(data instanceof Object)) {
        return;
    }
    for (let key in data) {
        const value = data[key];
        if (value == null) {
            break;
        }
        // check if attribute has a parser, and assign the parser or a dummy function
        const parser = parsers[key] || ((a) => a);
        // handle special cases
        switch (key) {
            case 'systemAttribute':
                const systemAttributes = parser(value);
                node[key] = systemAttributes;
                // inject favorites attribute to parent node
                node['favorite'] = parseBoolean(systemAttributes['favorite']);
                break;
            case 'versionCreated':
                // rename versionCreated to createdAt
                key = 'createdAt';
            default:
                const { $ } = value;
                node[key] = $ ? parser($) : traverse(data[key], node[key]);
        }
    }
    return node;
};
const parseLinks = (links = []) => {
    const alternates = [];
    let contentLink = '';
    (Array.isArray(links) ? links : [links]).forEach((link) => {
        if (link['@rel'] === 'content.alternate') {
            alternates.push({
                url: link.$,
                mimeType: link['@a:mimeType'],
                size: Number(link['@a:size']),
                width: Number(link['@a:width'] ?? 0),
                height: Number(link['@a:height'] ?? 0),
                bitrate: Number(link['@a:bitrate'] ?? 0),
                imageFormat: link['@a:imageFormat'],
            });
            return;
        }
        if (link['@rel'] === 'content') {
            contentLink = link.$;
        }
    });
    return { alternates, contentLink };
};
export const parsePagination = (links = []) => {
    const nextPage = (Array.isArray(links) ? links : [links]).find((l) => l['@rel'] === 'next');
    const nextPageParams = {};
    const hasNextPage = !!nextPage;
    if (hasNextPage) {
        new URL(nextPage.$).searchParams.forEach((value, key) => {
            nextPageParams[key] = value;
        });
    }
    return { hasNextPage, nextPageParams };
};
export const parseCollectionsPagination = (links = []) => {
    const nextPage = (Array.isArray(links) ? links : [links]).find((l) => l['rel'] === 'next');
    const nextPageParams = {};
    const hasNextPage = !!nextPage;
    if (hasNextPage) {
        new URL(nextPage.href).searchParams.forEach((value, key) => {
            nextPageParams[key] = value;
        });
    }
    return { hasNextPage, nextPageParams };
};
export const parsePaginationManual = (params, total) => {
    const { start, count } = params;
    const nextPageParams = { ...params };
    const hasNextPage = (start && count && start * count < total) || false;
    if (hasNextPage) {
        nextPageParams.start = start + 1;
    }
    return { hasNextPage, nextPageParams };
};
export const parseAttributes = (attrs = []) => {
    const attributes = {
        favorite: false,
        mimeType: undefined,
        takenAt: undefined,
        captureDate: undefined,
        artist: undefined,
        album: undefined,
        title: undefined,
        genre: undefined,
        year: 0,
        duration: 0,
        height: 0,
        width: 0,
    };
    (Array.isArray(attrs) ? attrs : [attrs]).forEach((attribute) => {
        switch (attribute['@name']) {
            case 'Favorite':
                attributes.favorite = Boolean(attribute.$);
                break;
            case 'Mime-Type':
                attributes.mimeType = attribute.$;
                break;
            case 'Capture-Date':
                attribute?.$ && (attributes.captureDate = attribute.$);
                break;
            case 'Timeline-Date':
                attribute?.$ && !attribute?.$.startsWith('-0091') && (attributes.takenAt = attribute.$);
                break;
            case 'Artist':
                attribute?.$ && attribute.$ !== '-' && (attributes.artist = attribute.$);
                break;
            case 'Album':
                attribute?.$ && attribute.$ !== '-' && (attributes.album = attribute.$);
                break;
            case 'Title':
                attribute?.$ && attribute.$ !== '-' && (attributes.title = attribute.$);
                break;
            case 'Genre':
                attribute?.$ && attribute.$ !== '-' && (attributes.genre = attribute.$);
                break;
            case 'Year':
                attribute?.$ && (attributes.year = Number(attribute.$));
                break;
            case 'Duration':
                attribute?.$ && (attributes.duration = Number(attribute.$));
                break;
            case 'Height':
                attribute?.$ && (attributes.height = Number(attribute.$));
                break;
            case 'Width':
                attribute?.$ && (attributes.width = Number(attribute.$));
                break;
            default:
        }
    });
    return attributes;
};
export const mergeAttributes = (...attrs) => {
    let attributes = [];
    attrs.forEach((a) => {
        if (Array.isArray(a)) {
            attributes = attributes.concat(a);
        }
        else if (a) {
            attributes.push(a);
        }
    });
    return attributes;
};
export function parseFileContainer(files = []) {
    return Array.isArray(files) ? files.map(({ file }) => file) : files.file;
}
export function parseFiles(files) {
    const { file = [] } = files;
    return (Array.isArray(file) ? file : [file]).map((item) => {
        const author = {
            lcid: item.author.lcid?.$,
            phone: item.author.phone?.$,
            email: item.author.email?.$,
            firstName: item.author.firstName?.$,
            lastName: item.author.lastName?.$,
        };
        return {
            nodeId: item.nodeId.$,
            author,
            name: item.name.$,
            size: Number(item.size.$),
            uri: item.uri.$,
            extension: item.extension.$,
            checksum: item.checksum?.$,
            repository: item.repository?.$,
            contentToken: item.contentToken?.$,
            createdAt: new Date(item.versionCreated.$).valueOf(),
            parentPath: item.parentPath.$,
            version: Number(item.version.$),
            ...parseLinks(item.link),
            ...parseAttributes(item.systemAttribute),
            ...(item.deletionDate ? { deletionDate: item.deletionDate.$ } : {}),
        };
    });
}
export function parseFolders(folders) {
    const { folder = [] } = folders;
    return (Array.isArray(folder) ? folder : [folder]).map((item) => {
        return traverse({ ...item, type: 'folder' }, {});
    });
}
export const getRepoName = (item, deviceType, showDeviceDisplayName) => {
    const devicedisplayname = item?.clientAttribute?.find((val) => val['@name']?.toLowerCase() === 'devicedisplayname')?.$ ||
        item.name?.$;
    if (deviceType?.toLowerCase() === 'desktop' || showDeviceDisplayName) {
        return devicedisplayname;
    }
    return item.name?.$;
};
export function parseRepositories(repositories, showDeviceDisplayName) {
    return (Array.isArray(repositories) ? repositories : [repositories]).map((item) => {
        const deviceType = item?.clientAttribute?.find((val) => val['@name']?.toLowerCase() === 'devicetype')?.$;
        return {
            name: getRepoName(item, deviceType, showDeviceDisplayName),
            uri: item.uri?.$,
            type: item.type?.$,
            size: item.totalUsage?.$,
            deviceType,
            createdAt: String(new Date(item.lastModified?.$).valueOf()),
        };
    });
}
export function parseCollection(collection, shared) {
    const collectionarray = Array.isArray(collection.collectionItems)
        ? collection.collectionItems
        : [collection.collectionItems];
    return ((collectionarray.length &&
        collectionarray.map((item) => {
            if (shared) {
                return { nodeUri: { $: item.uri } };
            }
            const [, , repositoryName, nodeId] = item.uri.split(':');
            return { '@repositoryName': repositoryName, nodeId: { $: nodeId } };
        })) ||
        []);
}
export function parseCollections(collections = []) {
    const collectionsarray = Array.isArray(collections) ? collections : [collections];
    return ((collectionsarray.length &&
        collectionsarray
            .filter((element) => {
            return element.coverItem !== undefined;
        })
            .map((item) => {
            const [, , repositoryName, nodeId] = item.coverItem?.split(':');
            return {
                '@repositoryName': repositoryName,
                nodeId: { $: nodeId },
                collectionId: item.id,
            };
        })) ||
        []);
}
export const parsePlaylistItems = (rawResponse) => {
    const { playlistElement = [] } = rawResponse.playlist;
    return (Array.isArray(playlistElement) ? playlistElement : [playlistElement]).map((p) => {
        return {
            pathId: p.pathId.$,
            nodeId: p.pathId.$,
            name: p.filename.$,
            size: Number(p.size.$),
            uri: p.uri.$,
            extension: p.extension.$,
            checksum: p.checksum?.$,
            contentToken: p.contentToken?.$,
            createdAt: String(new Date(p.versionCreated.$).valueOf()),
            repositoryPath: p.repositoryPath ? p.repositoryPath.$ : '',
            ...parseLinks(p.link),
            ...parseAttributes(mergeAttributes(p.systemAttribute, p.fileAttribute)),
            mimeType: p.mimeType.$,
            title: p.title?.$ && p.title.$ !== '-' ? p.title.$ : undefined,
            artist: p.artist?.$ && p.artist.$ !== '-' ? p.artist.$ : undefined,
            album: p.album?.$ && p.album.$ !== '-' ? p.album.$ : undefined,
        };
    });
};
export const parseRepositoryPaths = (paths) => {
    if (!paths) {
        return [];
    }
    const parse = ({ '@pathId': pathId, $: repositoryPath }) => ({
        pathId,
        repositoryPath,
    });
    return Array.isArray(paths) ? paths.map(parse) : [parse(paths)];
};
export const parsePlaylists = async (playlistDefinition = [], fetchCover) => {
    const playlists = (Array.isArray(playlistDefinition)
        ? playlistDefinition
        : [playlistDefinition]).map((p) => ({
        uid: p.uid.$,
        nodeId: p.uid.$,
        name: p.name.$,
        type: p.type.$,
        mimeTypeRegex: p.mimeTypeRegex.$,
        createdAt: String(new Date(p.creationDate.$).valueOf()),
        lastModifiedAt: p.lastModifiedDate.$,
        uri: p.uri.$,
        validItemsCount: Number(p.validPathCount.$) ?? 0,
        paths: parseRepositoryPaths(p.repositoryPath),
        mimeType: '',
        alternates: [],
        contentToken: '',
        contentLink: '',
        favorite: false,
        collectionData: {
            id: p.uid.$,
            name: p.name.$,
            type: p.type.$,
            source: '',
            creationDate: p.creationDate.$,
            lastModifiedDate: p.lastModifiedDate.$,
            mediaItemsCount: Number(p.validPathCount.$) ?? 0,
            coverItem: '',
        },
    }));
    if (fetchCover) {
        getPlaylistCovers(playlists);
    }
    return playlists;
};
export const parsePlaylistDefinition = (playlistDefinition) => {
    return {
        uid: playlistDefinition.uid.$,
        nodeId: playlistDefinition.uid.$,
        name: playlistDefinition.name.$,
        type: playlistDefinition.type.$,
        mimeTypeRegex: playlistDefinition.mimeTypeRegex.$,
        createdAt: String(new Date(playlistDefinition.creationDate.$).valueOf()),
        lastModifiedAt: playlistDefinition.lastModifiedDate.$,
        uri: playlistDefinition.uri.$,
        validItemsCount: Number(playlistDefinition.validPathCount.$) ?? 0,
        paths: parseRepositoryPaths(playlistDefinition.repositoryPath),
    };
};
export const parseFileVersions = (versions = []) => (Array.isArray(versions) ? versions : [versions]).map((v) => {
    return {
        uid: v.uid.$,
        size: Number(v.size.$) || 0,
        checksum: v.checksum?.$,
        deleted: Boolean(v.deleted.$),
        creationDate: v.creationDate.$,
        type: v.type.$,
        contentChanged: Boolean(v.contentChanged.$),
        attributesChanged: Boolean(v.attributesChanged.$),
        uri: v.uri.$,
        version: Number(v.version.$) || undefined,
        fromVersion: v.fromVersion ? Number(v.fromVersion.$) : undefined,
    };
});
export const parseTimeslots = (timeslots = []) => (Array.isArray(timeslots) ? timeslots : [timeslots]).map((t) => ({
    id: t['@id'],
    start: t['@start'],
    end: t['@end'],
    count: Number(t['@count']) ?? 0,
    type: t['@type'],
}));
export function parseStoreCategory(categories) {
    return (Array.isArray(categories) ? categories : [categories]).map((item) => {
        return {
            id: item.id,
            title: item.title,
            description: item.description,
            media: item.media,
            mimeType: item?.media?.type,
        };
    });
}
export function parseStoreProduct(products) {
    return (Array.isArray(products) ? products : [products]).map((item) => {
        return {
            id: item.id,
            title: item.title,
            description: item.description,
            media: item.media,
            minPrice: item.minPrice,
            maxPrice: item.maxPrice,
        };
    });
}
export function parseRecentOrder(recentOrder) {
    return (Array.isArray(recentOrder) ? recentOrder : [recentOrder]).map((item) => {
        const itemTitles = item.lineItems?.map((lineItem) => lineItem.title);
        return {
            id: item.id,
            count: item.noOfItems,
            title: item.createdAt,
            description: itemTitles.join(', '),
            media: [],
        };
    });
}
export function parseCartItems(lineItems) {
    return (Array.isArray(lineItems) ? lineItems : [lineItems]).map((item) => {
        return {
            price: item.price,
            id: item.id,
            originalTotal: item.originalTotal,
            productid: item.productid,
            quantity: item.quantity,
            title: item.title,
            totalDiscount: item.totalDiscount,
            variantid: item.variantid,
        };
    });
}
export function parseOrder(order) {
    return order.lineItems.map((item) => {
        return {
            // Todo: to be removed after price is available in API
            price: item.price,
            id: item.id,
            quantity: item.quantity,
            title: item.title,
        };
    });
}
